import { generateCertificatePdf } from '@components/certificate-pdf/index.ts';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getApiCertificateData, postApiCertificate } from '@routes/system/certificate/api/certificate.api.ts';
import { getCertificateFileName, mapCertificateDataToPdfData } from '@routes/system/certificate/utils.ts';
import { downloadBlob } from '@utils/download.ts';
import { extractError } from '@utils/slice.ts';

export interface CertificateState {
    error: Record<string, any> | null;
    isDownloadCertificatePdfPending: boolean;
}

const initialState: CertificateState = {
    error: null,
    isDownloadCertificatePdfPending: false,
};

export const downloadCertificatePdf = createAsyncThunk<void, number, { rejectValue: any }>(
    'certificate/downloadCertificatePdf',
    (resultId, { rejectWithValue }) =>
        getApiCertificateData(resultId)
            .then((data) => {
                const pdfData = mapCertificateDataToPdfData(data);
                const fileName = getCertificateFileName(pdfData);

                return generateCertificatePdf(pdfData)
                    .then((blob) => {
                        if (data.certificateContentId) {
                            return blob;
                        }

                        return postApiCertificate(resultId, blob, fileName).then(() => blob);
                    })
                    .then((blob) => {
                        downloadBlob(blob, fileName);
                    });
            })
            .catch((err) => rejectWithValue(err)),
);

const certificateSlice = createSlice({
    name: 'certificate',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(downloadCertificatePdf.pending, (state) => {
            state.error = null;
            state.isDownloadCertificatePdfPending = true;
        });
        builder.addCase(downloadCertificatePdf.fulfilled, (state) => {
            state.isDownloadCertificatePdfPending = false;
        });
        builder.addCase(downloadCertificatePdf.rejected, (state, action) => {
            state.error = extractError(action);
            state.isDownloadCertificatePdfPending = false;
        });
    },
});

export default certificateSlice.reducer;
